export enum FilterInstrumental {
  INCLUDE = 'include',
  EXCLUDE = 'exclude',
  REQUIRE = 'require',
}

export enum FilterAlbumType {
  ALBUM = 'album',
  SINGLE = 'single',
  COMPILATION = 'compilation',
  APPEARS_ON = 'appears_on'
}

export enum SetlistTrackType {
  OPENING = 'opening',
  NORMAL = 'normal',
  CLOSING = 'closing',
  ENCORE = 'encore'
}

export enum FollowItemType {
  ALBUM = 'album',
  PLAYLIST = 'playlist',
  ARTIST = 'artist'
}

export enum PageModuleType {
  CHART = 'chart',
  VIDEO = 'video',
  ADVERTISEMENT = 'advertisement',
  PLAYLIST = 'playlist',
  NEWSLETTER = 'newsletter',
  SOCIAL = 'social',
}

export interface PageModuleDataChart {
  title: string
  text: string
  buttonShare: string
  buttonOpen: string
  buttonRestart?: string
}

export interface PageModuleDataVideo {
  title: string
  videoUrl: string
}

export interface PageModuleDataAdvert {
  title: string
  imageUrl: string
  text: string
  buttonLink: string
  buttonLinkType: string
  buttonLinkShopeName: string
  button: string
}

export interface PageModuleDataPlaylist {
  title: string
  buttonSave: string
  buttonShare: string
  buttonOpen: string
  playlistHeight?: string
}

export interface PageModuleDataNewsletter {
  title: string
  terms: string
  button: string
  success: string
}

export interface PageModuleDataSocial {
  title: string
  youtube: string
  instagram: string
  twitter: string
  facebook: string
  tiktok: string
}

export type PageModuleData = PageModuleDataChart | PageModuleDataVideo | PageModuleDataAdvert | PageModuleDataPlaylist | PageModuleDataNewsletter | PageModuleDataSocial

export interface PageModule<T> {
  type: PageModuleType
  data: {
    [language: string]: T
  }
}

export interface FollowItem {
  service: 'spotify'
  id: string
  type: FollowItemType
}

export interface ExternalTrack {
  name: string
  artist_name: string
  album_name: string
  release_date: string // iso-date
  preview_url: string
  images: {
    width: number | null
    height: number | null
    url: string
  }[]
}

export type UserJourneyStepName = 'Home' | 'Newsletter' | 'Login' | 'Playlist' | 'Result'

export interface UserJourneyStep {
  position: number
  name: UserJourneyStepName
}

export interface CampaignUserJourney {
  steps: UserJourneyStep[]
}

export interface RestCampaignData {
  id?: number
  created_at?: string
  playlist_name: string
  artist_playlist: string | { spotify_id: string, account_id: string, account_connected?: boolean }
  artists: string[] | { spotify_id: string }[]
  playlists: string[] | { spotify_id: string }[]
  external_tracks?: ExternalTrack[]
  filters: {
    first_artist_only: boolean
    artist_album_types: FilterAlbumType[]
    instrumental: FilterInstrumental
  }
  is_competition: boolean
  setlist_normal_tracks_limit: number|null
  community_playlist_track_limit: number|null
  setlist_track_types: string[]
  streaming_services: string[]
  setlist_cover_image: string | null
  community_setlist_cover_image: string | null
  frontend: {
    page_modules_result: PageModule<PageModuleData>[]
    page_modules_end: PageModule<PageModuleData>[]
    page_modules_chart: PageModule<PageModuleData>[]
    campaignTrackNormalMin: number
    theme: {
      isDark: boolean
      primaryColor: string
      font: string
      logoImageUrl: string
      headerImageUrl: string
      backgroundImageUrl: string
      newsletterImageUrl: string
    }
  }
  follow: FollowItem[]
  user_journey: CampaignUserJourney
}

export interface MusicCoverImage {
  width: number | null
  height: number | null
  url: string
}

export interface RestTrack {
  name: string
  artist_name: string
  album_name: string
  release_date: string
  preview_url: string
  images: MusicCoverImage[]
  spotify_id: string|null
  deezer_id?: string|null
  disc_number?: number
  album_spotify_id: string
  track_number?: number
  playlist_name: string
  playlist_spotify_id: string
  playlist_pos: number
  playlist_images: {
    width: number | null
    height: number | null
    url: string
  }[]
  isrc?: string|null
  id: number
}

export interface ConnectedArtist {
  campaign_id: number
  connected_at: string
  display_name: string
  id: number
  streaming_service: string
  streaming_service_id: string
  token: string
}

// Owner of the artist playlist as recorded on the server when the playlist
// was saved to the campaign. playlistId ties the owner to that playlist so a
// locally changed (unsaved) playlist selection isn't checked against it.
export interface ArtistPlaylistOwner {
  playlistId: string
  accountId: string
}

export interface RestAuthUser {
  display_name: string
  email: string
  profile_image: string
  registered_at: string
  streaming_service: string
  streaming_service_country: string
  streaming_service_id: string
  spotify_access_token?: string | null
  spotify_access_token_expires_at?: string | null
}

export interface SpotifyAccessToken {
  access_token: string
  expires_at: string
}

export interface SetlistCover {
  image: string
}

export interface RestSetlisTrack {
  id: number
  track_type: SetlistTrackType
}

export interface RestSetlist {
  tracks: RestSetlisTrack[]
  test: boolean
  // Set when the client created the Spotify playlist itself; the backend then
  // records this id instead of creating the playlist.
  playlist_id?: string
}

export interface SetlistTrack {
  id: number
  track_type: string
  position: number
  position_change: number
  name: string
  artist_name: string
  preview_url: string
  album_name: string
  album_spotify_id: string
  spotify_id: string
  deezer_id: string
  images: {
    width: number | null
    height: number | null
    url: string
  }[]
  votes?: number
}

export interface RestSetlistGenerateResult {
  tracks: SetlistTrack[]
  playlist_streaming_service: 'spotify'
  playlist_name: string
  playlist_id: string
  id: number
}

export interface RestSetlistJob {
  job_id: string
}

export type RestSetlistJobState = 'pending' | 'in_progress' | 'completed' | 'failed' | 'not_found'

export interface RestSetlistJobStatus {
  job_id: string
  state: RestSetlistJobState
  setlist?: RestSetlistGenerateResult | null
  // Stable identifier when state === 'failed' (e.g. 'InvalidTracks', 'InternalError')
  error_type?: string | null
  error?: string | null
}

export interface RestSetlistResult {
  campaign_id: number
  playlist_ids: {
    spotify: string
  },
  tracks: SetlistTrack[]
  playlist_name: string
  playlist_updated_at: {
    spotify: string
  },
  total_votes: number
  cover_image: string | null
  spotify_snapshot_id: string
}

export interface RestCompetitonResult {
  display_name: string
  email: string
  country_code: string
  id: number
  signed_up_at: string
  user_id: number
  authenticated: boolean
}

export enum Font {
  MERRIWEATHER = 'Merriweather',
  POPPINS = 'Poppins',
  PT_MONO = 'PT Mono',
}

export interface TracksReloadJobState {
  type: 'job'
  state: 'pending'|'started'|'completed'
  success: boolean
  result: any|null
  error: string|null
  completed_at: string|null
}

export interface JourneyStep {
  position: number
  name: string
}
